/* eslint react/forbid-prop-types: 0, no-underscore-dangle: 0*/
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import trimCanvas from 'trim-canvas';

import Bezier from './bezier';
import Point from './point';

export default class SignatureCanvas extends Component {
  static propTypes = {
    velocityFilterWeight: PropTypes.number,
    minWidth: PropTypes.number,
    maxWidth: PropTypes.number,
    dotSize: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    penColor: PropTypes.string,
    onEnd: PropTypes.func,
    onBegin: PropTypes.func,
    canvasProps: PropTypes.object,
    clearOnResize: PropTypes.bool,
    backgroundColor: PropTypes.string,
  }

  static defaultProps = {
    velocityFilterWeight: 0.001,
    minWidth: 0.5,
    maxWidth: 2.5,
    dotSize: (minWidth, maxWidth) => (minWidth + maxWidth) / 2,
    penColor: 'black',
    onEnd: () => {},
    onBegin: () => {},
    backgroundColor: 'rgba(0,0,0,0)',
    clearOnResize: true,
  }

  componentDidMount() {
    this.contextS = this.canvasS.getContext('2d');

    this._handleMouseEvents();
    this._handleTouchEvents();
    this._resizeCanvas();
    this.clear();
  }

  componentWillUnmount() {
    this.off();
  }

  // return the canvas ref for operations like toDataURL
  getCanvas = () => this.canvasS

  // return a trimmed copy of the canvas
  getTrimmedCanvas = () => {
    // copy the canvas
    const copy = document.createElement('canvas');
    copy.width = this.canvasS.width;
    copy.height = this.canvasS.height;
    copy.getContext('2d').drawImage(this.canvasS, 0, 0);
    // then trim it
    return trimCanvas(copy);
  }

  clear = () => {
    const ctx = this.contextS;
    const canvas = this.canvasS;

    ctx.fillStyle = this.props.backgroundColor;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    this._reset();
  }

  fromDataURL = (dataURL, options) => {
    const image = new Image();
    const opts = options || {};
    const ratio = opts.ratio || window.devicePixelRatio || 1;
    const width = opts.width || this.canvasS.width / ratio;
    const height = opts.height || this.canvasS.height / ratio;

    this._reset();
    image.onload = () => this.contextS.drawImage(image, 0, 0, width, height);
    image.src = dataURL;
    this._isEmpty = false;
  }

  isEmpty = () => this._isEmpty

  _checkClearOnResize = () => {
    this._resizeCanvas();
  }

  _resizeCanvas = () => {
    let data;
    if (!this.props.clearOnResize) {
      data = !this.isEmpty() ? this.getCanvas().toDataURL() : '';
    }

    const ctx = this.contextS;
    const canvas = this.canvasS;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    /* When zoomed out to less than 100%, for some very strange reason,
      some browsers report devicePixelRatio as less than 1
      and only part of the canvas is cleared then. */
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    // get the space outside the canvas size
    const w = window.innerWidth - canvas.offsetWidth;
    const h = window.innerHeight - canvas.offsetHeight;

    canvas.width = (window.innerWidth - w) * ratio;
    canvas.height = (window.innerHeight - h - 98) * ratio;
    ctx.scale(ratio, ratio);
    this.clear();
    if (!this.props.clearOnResize && data !== '') {
      const options = {};
      options.ratio = ratio || '';
      this.fromDataURL(data, options);
    }
  }

  _reset = () => {
    this.points = [];
    this._lastVelocity = 0;
    this._lastWidth = (this.props.minWidth + this.props.maxWidth) / 2;
    this._isEmpty = true;
    this.contextS.fillStyle = this.props.penColor;
  }

  _handleMouseEvents = () => {
    this._mouseButtonDown = false;

    this.canvasS.addEventListener('mousedown', this._handleMouseDown);
    this.canvasS.addEventListener('mousemove', this._handleMouseMove);
    document.addEventListener('mouseup', this._handleMouseUp);

    window.addEventListener('resize', this._checkClearOnResize);
  }

  _handleTouchEvents = () => {
    // Pass touch events to canvas element on mobile IE and Edge.
    this.canvasS.style.msTouchAction = 'none';
    this.canvasS.style.touchAction = 'none';
    this.canvasS.addEventListener('touchstart', this._handleTouchStart);
    this.canvasS.addEventListener('touchmove', this._handleTouchMove);
    document.addEventListener('touchend', this._handleTouchEnd);
  }

  off = () => {
    this.canvasS.removeEventListener('mousedown', this._handleMouseDown);
    this.canvasS.removeEventListener('mousemove', this._handleMouseMove);
    document.removeEventListener('mouseup', this._handleMouseUp);

    this.canvasS.removeEventListener('touchstart', this._handleTouchStart);
    this.canvasS.removeEventListener('touchmove', this._handleTouchMove);
    document.removeEventListener('touchend', this._handleTouchEnd);

    window.removeEventListener('resize', this._checkClearOnResize);
  }

  _handleMouseDown = (ev) => {
    if (ev.which === 1) {
      this._mouseButtonDown = true;
      this._strokeBegin(ev);
    }
  }

  _handleMouseMove = (ev) => {
    if (this._mouseButtonDown) {
      this._strokeUpdate(ev);
    }
  }

  _handleMouseUp = (ev) => {
    if (ev.which === 1 && this._mouseButtonDown) {
      this._mouseButtonDown = false;
      this._strokeEnd(ev);
    }
  }

  _handleTouchStart = (ev) => {
    const touch = ev.changedTouches[0];
    this._strokeBegin(touch);
  }

  _handleTouchMove = (ev) => {
    // prevent scrolling
    ev.preventDefault();

    const touch = ev.changedTouches[0];
    this._strokeUpdate(touch);
  }

  _handleTouchEnd = (ev) => {
    const wasCanvasTouched = ev.target === this.canvasS;
    if (wasCanvasTouched) {
      this._strokeEnd(ev);
    }
  }

  _strokeUpdate = (ev) => {
    const point = this._createPoint(ev);
    this._addPoint(point);
  }

  _strokeBegin = (ev) => {
    this._reset();
    this._strokeUpdate(ev);
    this.props.onBegin(ev);
  }

  _strokeDraw = (point) => {
    const ctx = this.contextS;
    const dotSize = typeof (this.props.dotSize) === 'function'
      ? this.props.dotSize(this.props.minWidth, this.props.maxWidth)
      : this.props.dotSize;

    ctx.beginPath();
    this._drawPoint(point.x, point.y, dotSize);
    ctx.closePath();
    ctx.fill();
  }

  _strokeEnd = (ev) => {
    const canDrawCurve = this.points.length > 2;
    const point = this.points[0];

    if (!canDrawCurve && point) {
      this._strokeDraw(point);
    }

    this.props.onEnd(ev);
  }

  _createPoint = (ev) => {
    const rect = this.canvasS.getBoundingClientRect();
    return new Point(ev.clientX - rect.left, ev.clientY - rect.top);
  }

  _addPoint = (point) => {
    const points = this.points;
    let c2;
    let c3;
    let curve;
    let tmp;

    points.push(point);

    if (points.length > 2) {
      // To reduce the initial lag make it work with 3 points
      // by copying the first point to the beginning.
      if (points.length === 3) points.unshift(points[0]);

      tmp = this._calculateCurveControlPoints(points[0], points[1], points[2]);
      c2 = tmp.c2;
      tmp = this._calculateCurveControlPoints(points[1], points[2], points[3]);
      c3 = tmp.c1;
      curve = new Bezier(points[1], c2, c3, points[2]);
      this._addCurve(curve);

      // Remove the first element from the list,
      // so that we always have no more than 4 points in points array.
      points.shift();
    }
  }

  _calculateCurveControlPoints = (s1, s2, s3) => {
    const dx1 = s1.x - s2.x;
    const dy1 = s1.y - s2.y;
    const dx2 = s2.x - s3.x;
    const dy2 = s2.y - s3.y;

    const m1 = { x: (s1.x + s2.x) / 2.0, y: (s1.y + s2.y) / 2.0 };
    const m2 = { x: (s2.x + s3.x) / 2.0, y: (s2.y + s3.y) / 2.0 };

    const l1 = Math.sqrt((dx1 * dx1) + (dy1 * dy1));
    const l2 = Math.sqrt((dx2 * dx2) + (dy2 * dy2));

    const dxm = (m1.x - m2.x);
    const dym = (m1.y - m2.y);

    const k = l2 / (l1 + l2);
    const cm = { x: m2.x + (dxm * k), y: m2.y + (dym * k) };

    const tx = s2.x - cm.x;
    const ty = s2.y - cm.y;

    return {
      c1: new Point(m1.x + tx, m1.y + ty),
      c2: new Point(m2.x + tx, m2.y + ty),
    };
  };

  _addCurve = (curve) => {
    const startPoint = curve.startPoint;
    const endPoint = curve.endPoint;
    let velocity;

    velocity = endPoint.velocityFrom(startPoint);
    velocity = (this.props.velocityFilterWeight * velocity)
      + ((1 - this.props.velocityFilterWeight) * this._lastVelocity);

    const newWidth = this._strokeWidth(velocity);
    this._drawCurve(curve, this._lastWidth, newWidth);

    this._lastVelocity = velocity;
    this._lastWidth = newWidth;
  }

  _drawPoint = (x, y, size) => {
    const ctx = this.contextS;

    ctx.moveTo(x, y);
    ctx.arc(x, y, size, 0, 2 * Math.PI, false);
    this._isEmpty = false;
  }

  _drawCurve = (curve, startWidth, endWidth) => {
    const ctx = this.contextS;
    const widthDelta = endWidth - startWidth;
    let width;
    let t;
    let tt;
    let ttt;
    let u;
    let uu;
    let uuu;
    let x;
    let y;

    const drawSteps = Math.floor(curve.length());
    ctx.beginPath();
    for (let i = 0; i < drawSteps; i += 1) {
        // Calculate the Bezier (x, y) coordinate for this step.
      t = i / drawSteps;
      tt = t * t;
      ttt = tt * t;
      u = 1 - t;
      uu = u * u;
      uuu = uu * u;

      x = uuu * curve.startPoint.x;
      x += 3 * uu * t * curve.control1.x;
      x += 3 * u * tt * curve.control2.x;
      x += ttt * curve.endPoint.x;

      y = uuu * curve.startPoint.y;
      y += 3 * uu * t * curve.control1.y;
      y += 3 * u * tt * curve.control2.y;
      y += ttt * curve.endPoint.y;

      width = startWidth + (ttt * widthDelta);
      this._drawPoint(x, y, width);
    }
    ctx.closePath();
    ctx.fill();
  }

  _strokeWidth = velocity => Math.max(this.props.maxWidth / (velocity + 1), this.props.minWidth)

  render() {
    const { canvasProps } = this.props;
    return <canvas ref={(ref) => { this.canvasS = ref; }} {...canvasProps} />;
  }
}



// WEBPACK FOOTER //
// ./src/components/SignaturePad/SignaturePad.jsx